<template>
    <BaseDialog v-model:open="isOpen" title="Merge People" show-close float-close dialog-content-class="md:min-w-[480px]!">
        <template #trigger>
            <slot name="trigger"></slot>
        </template>
        <div class="p-5 flex flex-col gap-5">
            <div class="pr-8">
                <h3 class="title-small mb-1">Merge People</h3>
                <p class="body-small text-gray-500">
                    {{
                        isAnchored
                            ? `Combine ${currentName} with another person. All notes, sessions, and data will be merged into one record.`
                            : "Select two people to merge into one record. All notes, sessions, and data will be combined."
                    }}
                </p>
            </div>

            <!-- Step 1: Select connections -->
            <div v-if="!showConfirmation" class="flex flex-col gap-4">
                <!-- Anchored mode: current person is fixed, pick who to merge with + which to keep -->
                <template v-if="isAnchored">
                    <div class="flex flex-col gap-2">
                        <span class="body-small-semibold text-gray-700">Merge {{ currentName }} with</span>
                        <BaseSelect
                            :model-value="otherId"
                            :options="otherOptions"
                            :placeholder="isLoadingCandidates ? 'Loading people...' : 'Select a person...'"
                            trigger-class="w-full!"
                            aria-label="Select a person to merge with"
                            @update:model-value="handleOtherChange" />
                    </div>
                    <div v-if="otherId" class="flex flex-col gap-2">
                        <span class="body-small-semibold text-gray-700">Which record should we keep?</span>
                        <BaseSelect
                            :model-value="keepId"
                            :options="keepOptions"
                            placeholder="Select record to keep..."
                            trigger-class="w-full!"
                            aria-label="Select which record to keep"
                            @update:model-value="(val: string) => (keepId = val)" />
                    </div>
                </template>

                <!-- List mode: pick both sides freely -->
                <template v-else>
                    <div class="flex flex-col gap-2">
                        <span class="body-small-semibold text-gray-700">Keep (target)</span>
                        <BaseSelect
                            :model-value="targetId"
                            :options="candidateOptions"
                            placeholder="Select person to keep..."
                            trigger-class="w-full!"
                            aria-label="Select person to keep"
                            @update:model-value="handleTargetChange" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <span class="body-small-semibold text-gray-700">Merge into target (will be removed)</span>
                        <BaseSelect
                            :model-value="sourceId"
                            :options="sourceOptions"
                            placeholder="Select person to merge..."
                            trigger-class="w-full!"
                            aria-label="Select person to merge"
                            @update:model-value="handleSourceChange" />
                    </div>
                </template>

                <template v-if="resolvedKeepId && resolvedRemoveId">
                    <!-- Direction diagram: the person being removed (left) merges INTO the kept person (right). -->
                    <div class="flex items-stretch gap-2 sm:gap-3">
                        <div class="flex-1 min-w-0 rounded-lg border border-valence-coral-200 bg-valence-coral-600/8 p-3 flex flex-col items-center text-center gap-1.5">
                            <div
                                class="w-10 h-10 rounded-full bg-white border border-valence-coral-200 flex items-center justify-center body-small text-valence-coral-800 shrink-0"
                                aria-hidden="true">
                                {{ initialsById(resolvedRemoveId) }}
                            </div>
                            <p class="body-small-semibold truncate w-full">{{ removeName }}</p>
                            <span class="text-[11px] uppercase tracking-wide text-valence-coral-800 font-semibold">Will be removed</span>
                        </div>
                        <div class="flex flex-col items-center justify-center shrink-0 gap-1">
                            <button
                                type="button"
                                class="group flex items-center justify-center w-9 h-9 rounded-full border border-gray-300 bg-white text-gray-500 hover:text-valence-primary hover:border-valence-primary cursor-pointer transition-colors"
                                title="Swap merge direction"
                                aria-label="Swap merge direction"
                                @click="swapDirection">
                                <i class="bi bi-arrow-right text-lg group-hover:hidden" aria-hidden="true"></i>
                                <i class="bi bi-arrow-left-right text-lg hidden group-hover:inline" aria-hidden="true"></i>
                            </button>
                            <span class="text-[10px] text-gray-400 leading-none">Swap</span>
                        </div>
                        <div class="flex-1 min-w-0 rounded-lg border border-valence-green-600/40 bg-valence-green-600/8 p-3 flex flex-col items-center text-center gap-1.5">
                            <div
                                class="w-10 h-10 rounded-full bg-white border border-valence-green-600/40 flex items-center justify-center body-small text-valence-green-800 shrink-0"
                                aria-hidden="true">
                                {{ initialsById(resolvedKeepId) }}
                            </div>
                            <p class="body-small-semibold truncate w-full">{{ keepName }}</p>
                            <span class="text-[11px] uppercase tracking-wide text-valence-green-800 font-semibold">Will be kept</span>
                        </div>
                    </div>
                    <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-col gap-1.5">
                        <p class="body-small text-gray-700">
                            <i class="bi bi-journal-text mr-1 text-gray-500" aria-hidden="true"></i>
                            <strong>{{ removeName }}</strong
                            >'s notes, sessions, and history are merged into <strong>{{ keepName }}</strong
                            >.
                        </p>
                        <p class="body-small text-gray-700">
                            <i class="bi bi-person-check mr-1 text-gray-500" aria-hidden="true"></i>
                            <strong>{{ keepName }}</strong
                            >'s name, title, and core details are kept.
                        </p>
                        <p class="body-small text-valence-coral-800">
                            <i class="bi bi-exclamation-triangle mr-1" aria-hidden="true"></i>
                            <strong>{{ removeName }}</strong> is then removed. This cannot be undone.
                        </p>
                    </div>
                </template>
                <div class="flex justify-end gap-3 pt-2">
                    <BaseButton theme="secondary" @click="isOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" :disabled="!canContinue" @click="showConfirmation = true">Continue</BaseButton>
                </div>
            </div>

            <!-- Step 2: Confirm -->
            <div v-else class="flex flex-col gap-4">
                <!-- Same direction diagram as the selection step: removed (left) → kept (right). -->
                <div class="flex items-stretch gap-2 sm:gap-3">
                    <div class="flex-1 min-w-0 rounded-lg border border-valence-coral-200 bg-valence-coral-600/8 p-3 flex flex-col items-center text-center gap-1.5">
                        <div
                            class="w-10 h-10 rounded-full bg-white border border-valence-coral-200 flex items-center justify-center body-small text-valence-coral-800 shrink-0"
                            aria-hidden="true">
                            {{ initialsById(resolvedRemoveId) }}
                        </div>
                        <p class="body-small-semibold truncate w-full">{{ removeName }}</p>
                        <span class="text-[11px] uppercase tracking-wide text-valence-coral-800 font-semibold">Will be removed</span>
                    </div>
                    <div class="flex flex-col items-center justify-center text-gray-400 shrink-0" aria-hidden="true">
                        <i class="bi bi-arrow-right text-2xl" aria-hidden="true"></i>
                    </div>
                    <div class="flex-1 min-w-0 rounded-lg border border-valence-green-600/40 bg-valence-green-600/8 p-3 flex flex-col items-center text-center gap-1.5">
                        <div
                            class="w-10 h-10 rounded-full bg-white border border-valence-green-600/40 flex items-center justify-center body-small text-valence-green-800 shrink-0"
                            aria-hidden="true">
                            {{ initialsById(resolvedKeepId) }}
                        </div>
                        <p class="body-small-semibold truncate w-full">{{ keepName }}</p>
                        <span class="text-[11px] uppercase tracking-wide text-valence-green-800 font-semibold">Will be kept</span>
                    </div>
                </div>
                <p class="body-small text-gray-600">
                    All notes, goals, and session history from <strong>{{ removeName }}</strong> will be moved to <strong>{{ keepName }}</strong
                    >, then <strong>{{ removeName }}</strong> will be removed. This cannot be undone.
                </p>
                <div v-if="errorMessage" class="bg-valence-coral-600/8 border border-valence-coral-200 rounded-lg p-3">
                    <p class="body-small text-valence-coral-800">{{ errorMessage }}</p>
                </div>
                <div class="flex justify-end gap-3 pt-2">
                    <BaseButton theme="secondary" :disabled="isMerging" @click="handleConfirmBack">{{ startAtConfirmation ? "Cancel" : "Back" }}</BaseButton>
                    <BaseButton theme="primary" :disabled="isMerging" class="bg-valence-coral-600! hover:bg-valence-coral-800!" @click="handleMerge">
                        {{ isMerging ? "Merging..." : "Confirm Merge" }}
                    </BaseButton>
                </div>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import type { ConnectionDict } from "~vue/types/connection";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { computed, ref, watch } from "vue";

interface Props {
    // List mode: caller supplies the full set of connections to choose from.
    connections?: Array<ConnectionDict>;
    // Anchored mode: one connection is fixed (e.g. the person detail page) and the
    // candidate list is fetched on demand.
    currentConnection?: ConnectionDict;
    // List mode: pre-fill the keep/remove sides when opened programmatically
    // (e.g. drag-and-drop on the people grid). Applied each time the dialog opens.
    initialTargetId?: string;
    initialSourceId?: string;
    // Skip the selection step and open directly on the confirmation step. Used by the
    // drag-and-drop flow, where the two people are already chosen by the gesture.
    startAtConfirmation?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    merged: [result: { keptId: string; removedId: string }];
}>();

const isOpen = defineModel<boolean>("open", { default: false });
const isAnchored = computed(() => !!props.currentConnection);

const labelFor = (c: ConnectionDict) => c.persona?.name || c.connection?.job_title || "(unnamed)";

// Candidate connections: provided by the caller (list mode) or fetched (anchored mode).
const { data: fetchedConnections, execute: fetchCandidates, isFetching: isLoadingCandidates } = useFetch("/api/connections/", { immediate: false }).get().json<ConnectionDict[]>();

const candidates = computed<ConnectionDict[]>(() => props.connections ?? fetchedConnections.value ?? []);

// Used to resolve names for either side, including the anchored current connection
// (which may not appear in the candidate list while it is still loading).
const lookup = computed<ConnectionDict[]>(() => {
    const list = [...candidates.value];
    if (props.currentConnection && !list.some((c) => c.id === props.currentConnection!.id)) {
        list.push(props.currentConnection);
    }
    return list;
});
const nameById = (id: string) => {
    const c = lookup.value.find((x) => x.id === id);
    return c ? labelFor(c) : "";
};
const initialsById = (id: string) => {
    const name = nameById(id);
    return name ? getFirstLastInitials(name) : "?";
};

// --- List mode state ---
const targetId = ref("");
const sourceId = ref("");

const candidateOptions = computed(() => candidates.value.map((c) => ({ key: c.id, label: labelFor(c) })));
const sourceOptions = computed(() => candidateOptions.value.filter((opt) => opt.key !== targetId.value));

const handleTargetChange = (val: string) => {
    targetId.value = val;
    if (sourceId.value === val) {
        sourceId.value = "";
    }
};
const handleSourceChange = (val: string) => {
    sourceId.value = val;
};

// --- Anchored mode state ---
const otherId = ref("");
const keepId = ref("");

const otherOptions = computed(() => candidates.value.filter((c) => c.id !== props.currentConnection?.id).map((c) => ({ key: c.id, label: labelFor(c) })));
const keepOptions = computed(() => {
    if (!props.currentConnection || !otherId.value) {
        return [];
    }
    return [
        { key: props.currentConnection.id, label: `${labelFor(props.currentConnection)} (currently viewing)` },
        { key: otherId.value, label: nameById(otherId.value) },
    ];
});

const handleOtherChange = (val: string) => {
    otherId.value = val;
    // Default to keeping the person the user is currently viewing.
    keepId.value = props.currentConnection?.id ?? "";
};

// --- Resolved keep / remove (shared by both modes) ---
const resolvedKeepId = computed(() => (isAnchored.value ? keepId.value : targetId.value));
const resolvedRemoveId = computed(() => {
    if (!isAnchored.value) {
        return sourceId.value;
    }
    return [props.currentConnection?.id ?? "", otherId.value].find((id) => id && id !== keepId.value) ?? "";
});

// Keep the template references simple by aliasing to the resolved values.
const keepName = computed(() => nameById(resolvedKeepId.value));
const removeName = computed(() => nameById(resolvedRemoveId.value));
const currentName = computed(() => (props.currentConnection ? labelFor(props.currentConnection) : ""));

const canContinue = computed(() => !!resolvedKeepId.value && !!resolvedRemoveId.value && resolvedKeepId.value !== resolvedRemoveId.value);

// Flip which record is kept vs removed. In anchored mode this just moves `keepId` to the
// other party; in list mode it swaps the two dropdown selections.
const swapDirection = () => {
    if (!resolvedKeepId.value || !resolvedRemoveId.value) {
        return;
    }
    if (isAnchored.value) {
        keepId.value = resolvedRemoveId.value;
    } else {
        [targetId.value, sourceId.value] = [sourceId.value, targetId.value];
    }
};

const showConfirmation = ref(false);
const isMerging = ref(false);
const errorMessage = ref("");

const resetState = () => {
    targetId.value = "";
    sourceId.value = "";
    otherId.value = "";
    keepId.value = "";
    showConfirmation.value = false;
    errorMessage.value = "";
};

watch(isOpen, (open) => {
    if (open) {
        resetState();
        if (!props.connections && !fetchedConnections.value) {
            fetchCandidates();
        }
        if (!isAnchored.value) {
            targetId.value = props.initialTargetId ?? "";
            sourceId.value = props.initialSourceId ?? "";
            if (props.startAtConfirmation && targetId.value && sourceId.value) {
                showConfirmation.value = true;
            }
        }
    }
});

// In the drag flow there is no selection step to return to, so the secondary button
// cancels and closes the dialog. Otherwise it steps back to the person picker.
const handleConfirmBack = () => {
    if (props.startAtConfirmation) {
        isOpen.value = false;
    } else {
        showConfirmation.value = false;
    }
};

async function handleMerge() {
    isMerging.value = true;
    errorMessage.value = "";

    const { error } = await useFetch("/api/connections/merge/").post({ target_id: resolvedKeepId.value, source_id: resolvedRemoveId.value }).json();

    isMerging.value = false;

    if (error.value) {
        if (!isSessionEndedError(error.value)) {
            errorMessage.value = "Failed to merge. Please try again.";
        }
        return;
    }

    const result = { keptId: resolvedKeepId.value, removedId: resolvedRemoveId.value };
    isOpen.value = false;
    emit("merged", result);
}
</script>
